import Address from 'components/Address';
import Button from 'components/Button';
import Field from 'components/Form/Field';
import Layout from 'components/Layout';
import LinkButton from 'components/LinkButton';
import TooltipWrapper from 'components/Tooltip';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import {H2} from 'components/Typography';
import Value from 'components/Value';
import {FC, MouseEventHandler} from 'react';
import {twMerge} from 'tailwind-merge';
import {c} from 'ttag';
import {AddressType} from 'types/address';
import {PermissionResponse} from 'types/permissions';

export type ClientContactData = {
    address?: AddressType;
    department?: string;
    emailAddress?: string;
    familyName?: string;
    faxNumber?: string;
    givenName?: string;
    idClient: string;
    isCompany: boolean;
    isDefault: boolean;
    mid: string;
    name?: string;
    permissions: {
        canDelete: PermissionResponse;
        canEdit: PermissionResponse;
        canSetAsDefault: PermissionResponse;
    };
    personalTitle?: string;
    phoneNumber?: string;
};

export type ClientContactProps = {
    className?: string;
    isLoadingRemove?: boolean;
    isLoadingSetDefault?: boolean;
    onRemoveContact: MouseEventHandler;
    onSetDefaultContact: MouseEventHandler;
} & ClientContactData;

const ClientContact: FC<ClientContactProps> = ({
    address,
    className,
    department,
    emailAddress,
    familyName,
    faxNumber,
    givenName,
    idClient,
    isCompany,
    isDefault,
    isLoadingRemove,
    isLoadingSetDefault,
    mid,
    name,
    onRemoveContact,
    onSetDefaultContact,
    permissions,
    personalTitle,
    phoneNumber,
}) => {
    const displayedName =
        (isCompany
            ? name
            : givenName
              ? `${familyName} ${givenName}`
              : familyName) || '';

    const showEditContactMessage = () =>
        permissions.canEdit.allowed
            ? c('component').t`Edit Contact`
            : permissions.canEdit.failureReason;

    return (
        <div
            className={twMerge(
                'h-full break-words rounded-md border border-grey-200 bg-grey-100 px-6 pb-8 pt-6',
                className,
            )}
            role="complementary"
        >
            <div className="flex justify-between">
                <H2 className="m-0" hasLeftBorderDecoration={false}>
                    {isCompany
                        ? c('component').t`Company`
                        : c('component').t`Personal`}
                </H2>
                <div className="flex gap-2">
                    {isDefault && (
                        <TooltipIcon
                            className="p-1"
                            icon="favorite_solid"
                            message={c('component').t`Default Contact`}
                            size="base"
                        />
                    )}
                    {!isDefault && (
                        <TooltipWrapper
                            message={
                                permissions.canSetAsDefault.failureReason ||
                                c('component').t`Set as Default`
                            }
                        >
                            <Button
                                aria-label="set as default"
                                disabled={!permissions.canSetAsDefault.allowed}
                                icon="favorite"
                                isLoading={isLoadingSetDefault}
                                kind="secondary"
                                onClick={onSetDefaultContact}
                                size="xs"
                            />
                        </TooltipWrapper>
                    )}
                    <TooltipWrapper message={showEditContactMessage()}>
                        <LinkButton
                            aria-label="edit"
                            disabled={!permissions.canEdit.allowed}
                            href={window.Urls.client_contact_edit_view(
                                idClient,
                                mid,
                            )}
                            icon="edit"
                            kind="secondary"
                            size="xs"
                        >
                            {c('component').t`Edit`}
                        </LinkButton>
                    </TooltipWrapper>
                    <TooltipWrapper
                        message={
                            permissions.canDelete.failureReason ||
                            c('component').t`Delete Contact`
                        }
                    >
                        <Button
                            aria-label="delete contact"
                            disabled={!permissions.canDelete.allowed}
                            icon="trash"
                            isLoading={isLoadingRemove}
                            kind="destructive"
                            onClick={onRemoveContact}
                            size="xs"
                        />
                    </TooltipWrapper>
                </div>
            </div>
            <div className="space-y-8 pt-6">
                <Layout.KeyValue keyWidth={8}>
                    <Field label={c('component').t`Name`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: displayedName,
                            }}
                        />
                    </Field>
                    {!isCompany && (
                        <Field label={c('component').t`Title`} type="value">
                            <Value
                                value={{
                                    type: 'string',
                                    value: personalTitle || '',
                                }}
                            />
                        </Field>
                    )}
                    {department && (
                        <Field
                            label={c('component').t`Department`}
                            type="value"
                        >
                            <Value
                                value={{
                                    type: 'string',
                                    value: department,
                                }}
                            />
                        </Field>
                    )}
                </Layout.KeyValue>
                <Layout.KeyValue keyWidth={8}>
                    <Field label={c('component').t`Phone Number`} type="value">
                        {phoneNumber && (
                            <Value
                                value={{
                                    type: 'string',
                                    value: phoneNumber,
                                }}
                            />
                        )}
                    </Field>
                    <Field label={c('component').t`Fax Number`} type="value">
                        {faxNumber && (
                            <Value
                                value={{
                                    type: 'string',
                                    value: faxNumber,
                                }}
                            />
                        )}
                    </Field>
                    <Field label={c('component').t`Email`} type="value">
                        {emailAddress && (
                            <Value
                                value={{
                                    type: 'string',
                                    value: emailAddress,
                                }}
                            />
                        )}
                    </Field>
                </Layout.KeyValue>
                <Layout.KeyValue keyWidth={8}>
                    <Field label={c('component').t`Address`} type="value">
                        {address && <Address address={address} />}
                    </Field>
                </Layout.KeyValue>
            </div>
        </div>
    );
};

export default ClientContact;
